/**
 * What the note model's parts kept of the score, and of themselves, beside the check's faults.
 *
 * The check counts faults against the book's rules. It does not say whether the parts follow
 * the score they were written under, or whether each part keeps to a figure once it has one;
 * a listener hears both before any fault. So a run of the note model counts them
 * (`ArrangeFollowed`), of the parts as checked, and the strip shows them as shares: how much of
 * what the parts sound and strike is the score's accompaniment's, and in how many bars a part
 * plays the bar before's shape again. They are counts to read a run by, not a verdict: a part
 * may rightly leave the score or its figure. A score with no accompaniment where the parts
 * sound gives nothing to follow, and the strip says so rather than print a share of nothing.
 */

import type { ArrangeFollowed as Counts } from "@app/shared-types";
import { count } from "../lib/format";
import { Figure } from "./ReadingParts";

/** `part` of `whole` as a whole percentage, or a dash where there is nothing to take it of. */
function share(part: number, whole: number): string {
  return whole === 0 ? "–" : `${Math.round((100 * part) / whole)}%`;
}

export default function ArrangeFollowed({ counts }: { readonly counts: Counts }): JSX.Element {
  const follows =
    counts.quarters === 0
      ? "The score has no accompaniment where the model's parts sound, so there is none to follow."
      : `Of the score's accompaniment, the model's parts as checked keep ${count(counts.shared)} of the ${count(counts.sounded)} pitch classes they sound, quarter by quarter, and its lowest note in ${count(counts.bass)} of ${count(counts.quarters)} quarters; they strike with it at ${count(counts.met)} of their ${count(counts.struck)} moments, of its ${count(counts.moments)}. Harmony, then bass, then rhythm.`;
  return (
    <>
      <Figure
        label="Follows"
        value={
          counts.quarters === 0
            ? "–"
            : `${share(counts.shared, counts.sounded)} · ${share(counts.bass, counts.quarters)} · ${share(counts.met, counts.struck)}`
        }
        title={follows}
      />
      <Figure
        label="Figures"
        value={`${share(counts.repeated, counts.after)} · ${share(counts.recalled, counts.shaped)}`}
        title={`A part plays the shape of its bar before again in ${count(counts.repeated)} of ${count(counts.after)} bars, and a shape it has played before in ${count(counts.recalled)} of its ${count(counts.shaped)} bars of three notes or more. The composers of the quartets held out from the fitting repeat the bar before in about a tenth of theirs; far less is a part with a new idea every bar.`}
      />
    </>
  );
}
